import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Globe, Clock3, RefreshCw } from "lucide-react";
import { API, authHeaders } from "./shared";

const PAGE_NAMES = {
  "/": "Home",
  "/shop": "Shop",
  "/portfolio": "Portfolio",
  "/pricing": "Pricing Calculator",
  "/style-finder": "Style Finder",
  "/boho-braids": "Boho Braids",
  "/waitlist": "Waitlist",
  "/consent-policy": "Consent Policy",
};

const fmtHour = (h) => {
  if (h === null || h === undefined) return "—";
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr} ${ampm}`;
};

const TrafficInsightsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/traffic-insights`, authHeaders())
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const maxPage = Math.max(...data.top_pages.map((p) => p.views), 1);
  const maxHour = Math.max(...data.hours.map((h) => h.views), 1);

  return (
    <div data-testid="traffic-insights-panel" className="mt-10 border border-brand-border bg-brand-surface/50 p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-serif text-lg text-brand-text flex items-center gap-2">
          <Globe size={16} className="text-brand-gold" /> Traffic Insights <span className="font-sans text-xs text-brand-text-muted font-normal">(last 7 days)</span>
        </h3>
        <button onClick={load} disabled={loading} data-testid="traffic-refresh-btn"
          className="flex items-center gap-1.5 font-sans text-[11px] px-3 py-1.5 border border-brand-border text-brand-text-muted hover:border-brand-gold hover:text-brand-gold transition-colors disabled:opacity-50">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <p className="font-sans text-xs text-brand-text-muted mb-5">
        {data.total_views_7d} page views tracked
        {data.busiest_hour !== null && (
          <> · busiest hour: <span className="text-brand-gold font-semibold" data-testid="busiest-hour">{fmtHour(data.busiest_hour)}</span> ({data.timezone})</>
        )}
      </p>

      {data.total_views_7d === 0 ? (
        <p data-testid="traffic-empty" className="font-sans text-sm text-brand-text-muted py-6 text-center">
          No visits tracked yet — data appears as soon as people browse the site.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div data-testid="top-pages-list">
            <p className="font-sans text-[10px] uppercase tracking-wider text-brand-text-muted mb-3">Top Pages</p>
            <div className="space-y-2">
              {data.top_pages.map((p) => (
                <div key={p.page} data-testid={`top-page-${p.page}`} className="flex items-center gap-3">
                  <span className="font-sans text-xs text-brand-text w-36 truncate" title={p.page}>
                    {PAGE_NAMES[p.page] || p.page}
                  </span>
                  <div className="flex-1 h-4 bg-brand-bg border border-brand-border/50">
                    <div className="h-full bg-brand-gold/70" style={{ width: `${(p.views / maxPage) * 100}%` }} />
                  </div>
                  <span className="font-sans text-xs text-brand-gold font-semibold w-10 text-right">{p.views}</span>
                </div>
              ))}
            </div>
          </div>

          <div data-testid="busiest-hours-chart">
            <p className="font-sans text-[10px] uppercase tracking-wider text-brand-text-muted mb-3">Visits by Hour</p>
            <div className="flex items-end gap-[2px] h-28">
              {data.hours.map((h) => (
                <div key={h.hour} className="flex-1 flex flex-col items-center" title={`${fmtHour(h.hour)}: ${h.views} views`}>
                  <div
                    className={`w-full ${h.hour === data.busiest_hour ? "bg-brand-gold" : "bg-brand-gold/40"}`}
                    style={{ height: `${Math.max((h.views / maxHour) * 96, h.views > 0 ? 4 : 1)}px` }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1.5">
              {["12A", "6A", "12P", "6P", "11P"].map((l) => (
                <span key={l} className="font-sans text-[9px] text-brand-text-muted">{l}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafficInsightsPanel;
